import { volumeDisplayName } from '../formatBytes';
import { effectiveSecondary, secondaryBucketKey } from '../secondaryGrouping';
import type { ExplorationSecondary, ExplorationVolume } from '../../types/exploration';

export interface StructuralHierarchyNode {
  type: string;
  name: string;
  count?: number;
  value?: number;
  volume?: ExplorationVolume;
  _volumes?: ExplorationVolume[];
  children?: StructuralHierarchyNode[];
}

export function sumStorage(vols: ExplorationVolume[]): number {
  return vols.reduce((s, v) => s + (v.size || 0), 0);
}

export function volumeNode(v: ExplorationVolume): StructuralHierarchyNode {
  return {
    type: 'volume',
    name: volumeDisplayName(v),
    volume: v,
    count: 1,
    value: v.size || 0,
  };
}

export function statusGroupNode(
  name: string,
  vols: ExplorationVolume[]
): StructuralHierarchyNode {
  const sortedVols = [...vols].sort((a, b) => (b.size || 0) - (a.size || 0));
  return {
    type: 'status',
    name,
    count: sortedVols.length,
    value: sumStorage(sortedVols),
    children: sortedVols.map(volumeNode),
  };
}

export function instanceNode(name: string, vols: ExplorationVolume[]): StructuralHierarchyNode {
  const sortedVols = [...vols].sort((a, b) => (b.size || 0) - (a.size || 0));
  return {
    type: 'instance',
    name,
    count: sortedVols.length,
    value: sumStorage(sortedVols),
    children: sortedVols.map(volumeNode),
  };
}

export function nodeGroupNode(name: string, vols: ExplorationVolume[]): StructuralHierarchyNode {
  const byInst = new Map<string, ExplorationVolume[]>();
  vols.forEach((v) => {
    const k = v.instanceId || '(no instance)';
    if (!byInst.has(k)) byInst.set(k, []);
    byInst.get(k)!.push(v);
  });
  const children = [...byInst.entries()].map(([id, iv]) => instanceNode(id, iv));
  children.sort((a, b) => (b.value || 0) - (a.value || 0));
  return { type: 'nodeGroup', name, count: vols.length, value: sumStorage(vols), children };
}

export function clusterNode(name: string, vols: ExplorationVolume[]): StructuralHierarchyNode {
  const byNg = new Map<string, ExplorationVolume[]>();
  vols.forEach((v) => {
    const k = v.nodeGroup || '(No Group)';
    if (!byNg.has(k)) byNg.set(k, []);
    byNg.get(k)!.push(v);
  });
  const children = [...byNg.entries()].map(([ng, nv]) => nodeGroupNode(ng, nv));
  children.sort((a, b) => (b.value || 0) - (a.value || 0));
  return { type: 'cluster', name, count: vols.length, value: sumStorage(vols), children };
}

export function buildClusterTopLevel(vols: ExplorationVolume[]): StructuralHierarchyNode[] {
  const k8s = new Map<string, ExplorationVolume[]>();
  const ec2Vols: ExplorationVolume[] = [];
  const unattached: ExplorationVolume[] = [];

  vols.forEach((v) => {
    if (v.cluster) {
      if (!k8s.has(v.cluster)) k8s.set(v.cluster, []);
      k8s.get(v.cluster)!.push(v);
    } else if (v.instanceId) {
      ec2Vols.push(v);
    } else {
      unattached.push(v);
    }
  });

  const top: StructuralHierarchyNode[] = [];
  k8s.forEach((vs, name) => top.push(clusterNode(name, vs)));

  if (ec2Vols.length) {
    const byStatus = new Map<string, ExplorationVolume[]>();
    ec2Vols.forEach((v) => {
      const k = v.supportLabel || v.supportStatus || '(unknown)';
      if (!byStatus.has(k)) byStatus.set(k, []);
      byStatus.get(k)!.push(v);
    });
    const children = [...byStatus.entries()].map(([label, vs]) => statusGroupNode(label, vs));
    children.sort((a, b) => (b.value || 0) - (a.value || 0));
    top.push({
      type: 'ec2',
      name: 'EC2 Instances',
      count: ec2Vols.length,
      value: sumStorage(ec2Vols),
      children,
    });
  }

  if (unattached.length) {
    const sortedVols = [...unattached].sort((a, b) => (b.size || 0) - (a.size || 0));
    top.push({
      type: 'unattached',
      name: 'Unattached',
      count: unattached.length,
      value: sumStorage(unattached),
      children: sortedVols.map(volumeNode),
    });
  }

  top.sort((a, b) => (b.value || 0) - (a.value || 0));
  return top;
}

export function buildSecondaryTopLevel(
  volumes: ExplorationVolume[],
  secondary: ExplorationSecondary,
  tagKey?: string
): StructuralHierarchyNode[] {
  if (secondary === 'cluster' || secondary === 'none') {
    return buildClusterTopLevel(volumes);
  }

  const keyOf = (v: ExplorationVolume) =>
    secondary === 'region'
      ? secondaryBucketKey(v, 'region', tagKey ?? '')
      : secondaryBucketKey(v, 'tag', tagKey ?? '');

  const buckets = new Map<string, ExplorationVolume[]>();
  volumes.forEach((v) => {
    const k = keyOf(v);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k)!.push(v);
  });

  const topChildren: StructuralHierarchyNode[] = [];
  buckets.forEach((vols, name) => {
    topChildren.push({
      type: secondary,
      name,
      count: vols.length,
      value: sumStorage(vols),
      children: buildClusterTopLevel(vols),
    });
  });
  topChildren.sort((a, b) => (b.value || 0) - (a.value || 0));
  return topChildren;
}

export interface ExplorationTopGroup {
  name: string;
  type: string;
  kind: string;
  _volumes: ExplorationVolume[];
}

export function bucketExplorationTopGroups(
  volumes: ExplorationVolume[],
  secondary: ExplorationSecondary,
  tagKey?: string
): ExplorationTopGroup[] {
  const groups: ExplorationTopGroup[] = [];
  secondary = effectiveSecondary(secondary, tagKey ?? '');

  if (secondary === 'cluster' || secondary === 'none') {
    const cm = new Map<string, ExplorationVolume[]>();
    const ec2Vols: ExplorationVolume[] = [];
    const unattached: ExplorationVolume[] = [];

    volumes.forEach((v) => {
      if (v.cluster) {
        if (!cm.has(v.cluster)) cm.set(v.cluster, []);
        cm.get(v.cluster)!.push(v);
      } else if (v.instanceId) {
        ec2Vols.push(v);
      } else {
        unattached.push(v);
      }
    });

    cm.forEach((vols, name) =>
      groups.push({ name, type: 'cluster', kind: 'cluster', _volumes: vols })
    );
    if (ec2Vols.length) {
      groups.push({
        name: 'EC2 Instances',
        type: 'ec2',
        kind: 'ec2',
        _volumes: ec2Vols,
      });
    }
    if (unattached.length) {
      groups.push({
        name: 'Unattached',
        type: 'unattached',
        kind: 'unattached',
        _volumes: unattached,
      });
    }
    return groups;
  }

  const keyOf = (v: ExplorationVolume) =>
    secondary === 'region'
      ? secondaryBucketKey(v, 'region', tagKey ?? '')
      : secondaryBucketKey(v, 'tag', tagKey ?? '');

  const m = new Map<string, ExplorationVolume[]>();
  volumes.forEach((v) => {
    const k = keyOf(v);
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(v);
  });

  m.forEach((vols, name) =>
    groups.push({ name, type: secondary, kind: 'flat', _volumes: vols })
  );
  return groups;
}

export function accountKey(v: ExplorationVolume): string {
  return v.accountName || v.accountId || '(unknown account)';
}

export function groupVolumesByAccount(volumes: ExplorationVolume[]): Map<string, ExplorationVolume[]> {
  const buckets = new Map<string, ExplorationVolume[]>();
  volumes.forEach((v) => {
    const k = accountKey(v);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k)!.push(v);
  });
  return buckets;
}
